import { Container, Divider } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ButtonGroupTop,
  CustomTable,
  ModalError,
  ModalSuccess,
  ModalWarning,
  Page,
  PrimaryButton,
  PrimaryDeleteButton,
  PrimaryRestoreButton,
  SearchTextInput,
  SelectInput,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { Fragment, useCallback, useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { emptyText, getErrors } from "utils";
import ModalFormUserPublisher from "./components/ModalFormUserPublisher";

function UserPublisher() {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(true);
  const [optionPublisher, setOptionPublisher] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
    role_id: "",
    type: "publisher",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalPreload, setModalPreload] = useState(null);
  const [optionRole, setOptionRole] = useState([]);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleActivate = item => {
    const { is_active_flag, name } = item;
    ModalWarning(
      `Are you sure to ${is_active_flag ? "deactivate" : "restore"} ${name} ?`,
      `${is_active_flag ? "Deactivate" : "Restore"} User Publisher`
    ).then(res => res?.isConfirmed && handleActivateUser(item));
  };

  const getOptionPublisher = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/publisher`, {
        headers,
        params: { is_active_flag: true },
      });
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        id: item?.publisher_id,
        label: item?.name,
      }));
      setOptionPublisher(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getOptionRole = () => {
    try {
      const res = axios.get(`${hardBaseUrl}/role?type=publisher`, {
        headers,
      });
      setOptionRole(res?.data?.data);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/user`, {
        headers,
        params: queryParams,
      });
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );
  const handleActivateUser = async item => {
    const { is_active_flag, user_id } = item;
    try {
      setLoadingPage(true);
      if (is_active_flag) {
        await axios.delete(`${hardBaseUrl}/user/${user_id}`, {
          headers,
        });
      } else {
        await axios.put(
          `${hardBaseUrl}/user-recovery/${user_id}`,
          {},
          { headers }
        );
      }
      ModalSuccess(
        `Successfully ${
          is_active_flag ? "Deactivate" : "Restore"
        } User Publisher`
      ).then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    getOptionPublisher();
    getOptionRole();
  }, []);

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="User Publisher">
      <Container maxWidth={false}>
        <HeaderTitle title="Parameter" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Fragment>
          <Grid container justifyContent="space-between" my="16px">
            <Grid item>
              <SearchTextInput
                placeholder="Search"
                value={queryParams?.search}
                onChange={e =>
                  handleChangeQueryParams(e?.target?.value, "search")
                }
                disabled={loadingPage}
              />
            </Grid>
            <Grid item>
              <Grid container columnSpacing={1} alignItems="center">
                <Grid item>
                  <SelectInput
                    label="Role"
                    options={optionRole}
                    optionKey="role_id"
                    optionLabel="name"
                    value={queryParams?.role_id || ""}
                    onChange={e =>
                      handleChangeQueryParams(e?.target?.value, "role_id")
                    }
                    placeholder="All Role"
                    width={200}
                    disabled={loadingPage}
                  />
                </Grid>
                <Grid item>
                  <PrimaryButton
                    onClick={() => {
                      setModalVisible(true);
                      setModalPreload(null);
                    }}
                    label="Add User Publisher"
                    startIcon={<Add />}
                    size="large"
                    disabled={loadingPage}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <CustomTable
            isLoading={loadingPage}
            columnTable={columnTable}
            data={dataTable}
            page={queryParams?.page}
            pageSize={queryParams?.size}
            pageCount={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangePageSize={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
            isHaveAction
            handleEdit={item => {
              setModalVisible(true);
              setModalPreload(item?.user_id);
            }}
            customAction={item =>
              item?.is_active_flag ? (
                <PrimaryDeleteButton
                  tooltipTitle="Deactivate User Publisher"
                  onClick={() => handleActivate(item)}
                />
              ) : (
                <PrimaryRestoreButton
                  tooltipTitle="Restore User Publisher"
                  onClick={() => handleActivate(item)}
                />
              )
            }
          />
        </Fragment>
        {modalVisible && (
          <ModalFormUserPublisher
            open={modalVisible}
            onClose={() => setModalVisible(false)}
            optionPublisher={optionPublisher}
            getData={getDataTable}
            preload={modalPreload}
          />
        )}
      </Container>
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Parameter",
    link: "/admin/parameter/user-publisher",
  },
  {
    label: "User Publisher",
    active: true,
  },
];
const columnTable = [
  {
    name: "name",
    title: "User Name",
  },
  {
    name: "all",
    title: "Role",
    renderText: item => emptyText(item?.role?.name),
  },
  {
    name: "all",
    title: "Publisher",
    renderText: item => emptyText(item?.publisher?.name),
  },
  {
    name: "phone",
    title: "Phone Number",
  },
  {
    name: "email",
    title: "Email",
  },
];

export default UserPublisher;
